import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { NextIntlClientProvider } from "next-intl";
import { ToastProvider } from "@navi-soft/ui/toast";
import { routeTree } from "./routeTree.gen";
import messagesRu from "./i18n/messages/ru.json";
import "@/styles/globals.css";

const queryClient = new QueryClient();

/**
 * Keep query values as raw strings. Default TanStack parse uses JSON.parse,
 * which turns Zitadel snowflake IDs (userID/orgID) into JS numbers and loses
 * precision — invite then sees empty userID and shows «ссылка неполная».
 */
function parseSearch(searchStr: string): Record<string, string> {
  const q = searchStr.startsWith("?") ? searchStr.slice(1) : searchStr;
  const out: Record<string, string> = {};
  for (const [key, value] of new URLSearchParams(q).entries()) {
    out[key] = value;
  }
  return out;
}

function stringifySearch(search: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}

const router = createRouter({
  routeTree,
  parseSearch,
  stringifySearch,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById("root");
if (!rootEl) {
  throw new Error("Missing #root element");
}

createRoot(rootEl).render(
  <StrictMode>
    <NextIntlClientProvider locale="ru" messages={messagesRu}>
      <QueryClientProvider client={queryClient}>
        <ToastProvider
          labels={{
            regionLabel: "Уведомления",
            successTitle: "Готово",
            errorTitle: "Ошибка",
            closeAria: "Закрыть",
          }}
        >
          <RouterProvider router={router} />
        </ToastProvider>
      </QueryClientProvider>
    </NextIntlClientProvider>
  </StrictMode>,
);
